// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Description
//
// Include description of object & useful info here, please!
/* eslint-disable camelcase, class-methods-use-this */

import {
  sharpMajors,
  flatMajors,
  sharpMinors,
  flatMinors,
} from 'shared/definitions/musicTheory/keySignatures.ts';
import Note from '../uTheory.Notation/note.ts';
import Chord from '../uTheory.Notation/chord.ts';
import Interval from '../uTheory.Notation/interval.ts';
import Scale from '../uTheory.Notation/scale.ts';
import Rhythm from '../uTheory.Notation/rhythm.ts';
import RomanNumeral from '../uTheory.Notation/romanNumeral.ts';
import * as Skills from './skills.ts';
import utils from '../utils.ts';
import IntervalQuestion from './intervalQuestion.ts';
import rejectDuplicates from './rejectDuplicates.ts';
import { generateRomanQuestions, getRNSkills } from './romanNumeralQuestionGenerator.ts';
import { randomWhiteNotes, randomNotes } from './randomNoteGenerators';
import { randomIntervalQuestions } from './randomIntervalGenerators';

class QuestionGenerator {
  // Public constructor
  constructor(skills, numberOfQuestions, options) {
    this.debug = false;
    if (arguments.length !== 0) this.init(skills, numberOfQuestions, options);
  }

  // Public Functions
  log(...rest) {
    if (!this.debug) return;
    rest.forEach((item) => {
      console.log(item);
    });
  }

  init(skills, numberOfQuestions, options) {
    this.questions = this.generate(skills, numberOfQuestions, options);
  }

  generate(skills, numberOfQuestions, options) {
    // If we're generating questions for multiple skills:
    if (skills instanceof Array) {
      const skillsObjects = [];
      // Convert any integer id's to string id's so we can
      // access their same-named methods as this[skill]
      skills.forEach((skill, index) => {
        skills[index] = Skills.makeString(skill);
        skillsObjects.push({ skill: skills[index] });
      });
      // And send this to our multiple skill helper:
      return this.multipleSkillsGenerate(skillsObjects, numberOfQuestions, options);
      // Otherwise, if it's just one skill
    }
    // Be sure it's in string-id format:
    skills = Skills.makeString(skills);
    const questions = this[skills](numberOfQuestions, options);
    return this.rejectDuplicates(questions, numberOfQuestions, options);
  }

  piano_c_g(numberOfQuestions, options) {
    if (this.debug) {
      console.log('options passed: ');
      console.log(options);
    }
    const acceptableWhiteNotes = ['C', 'D', 'E', 'F', 'G'];
    const whiteNotes = randomWhiteNotes(
      numberOfQuestions,
      options.lowestNote,
      options.highestNote,
      acceptableWhiteNotes,
      options
    );
    return whiteNotes;
  }

  piano_a_b(numberOfQuestions, options) {
    if (this.debug) {
      console.log('options passed: ');
      console.log(options);
    }

    return randomWhiteNotes(
      numberOfQuestions,
      options.lowestNote,
      options.highestNote,
      ['A', 'B'],
      options
    );
  }

  lines_spaces(numberOfQuestions, options) {
    options.clefs = options.clefs || [options.clef] || ['treble'];
    let i = 1;
    let top = 5;
    if (typeof (typeof options.legers !== 'undefined') && options.legers === true) {
      i = -1;
      top = 7;
    }
    if (typeof (typeof options.highLegers !== 'undefined') && options.highLegers === true) {
      top = 7;
    }
    if (typeof (typeof options.lowLegers !== 'undefined') && options.lowLegers === true) {
      i = -1;
    }
    let noStaff = false;
    if (options.noStaff) {
      noStaff = true;
    }

    const acceptableLines = [];
    for (; i <= top; i += 0.5) {
      // eslint-disable-next-line no-continue
      if (noStaff && i > 0.5 && i < 5) continue;
      acceptableLines.push(i);
    }

    this.log(`acceptableLines: ${acceptableLines}`);

    const possibleQuestions = acceptableLines
      .map((line) =>
        options.clefs.map((clef) => ({
          clef,
          line,
        }))
      )
      .reduce((a, b) => a.concat(b), []);

    this.log(possibleQuestions);

    return this.rejectDuplicates(possibleQuestions, numberOfQuestions, options);
  }

  treble_c_g(numberOfQuestions, options) {
    const lowestNote = 'C4';
    const highestNote = 'G4';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  treble_a_g(numberOfQuestions, options) {
    const lowestNote = 'A4';
    const highestNote = 'G5';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  treble_low_legers(numberOfQuestions, options) {
    // Drills both high & low together -- otherwise too few questions
    const lowestNote = 'G3';
    const highestNote = 'B3';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  treble_high_legers(numberOfQuestions, options) {
    const lowestNote = 'A5';
    const highestNote = 'D6';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  soprano_clef(numberOfQuestions, options) {
    const lowestNote = 'F3';
    const highestNote = 'A5';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  mezzo_soprano_clef(numberOfQuestions, options) {
    const lowestNote = 'D3';
    const highestNote = 'F5';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  alto_clef(numberOfQuestions, options) {
    const lowestNote = 'B2';
    const highestNote = 'D5';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  tenor_clef(numberOfQuestions, options) {
    const lowestNote = 'G2';
    const highestNote = 'B4';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  baritone_clef(numberOfQuestions, options) {
    const lowestNote = 'E2';
    const highestNote = 'G4';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  naturals(numberOfQuestions, options) {
    return randomNotes(numberOfQuestions, options.lowestNote, options.highestNote, ['']);
  }

  sharps(numberOfQuestions, options) {
    const highestNote = Interval.getNoteAtGenericInterval(options.highestNote, -2);

    return randomNotes(numberOfQuestions, options.lowestNote, highestNote, ['#']);
  }

  flats(numberOfQuestions, options) {
    const lowestNote = Interval.getNoteAtGenericInterval(options.lowestNote, 2);
    return randomNotes(numberOfQuestions, lowestNote, options.highestNote, ['b']);
  }

  double_sharps(numberOfQuestions, options) {
    const highestNote = Interval.getNoteAtGenericInterval(options.highestNote, -2);
    return randomNotes(numberOfQuestions, options.lowestNote, highestNote, ['x']);
  }

  double_flats(numberOfQuestions, options) {
    const lowestNote = Interval.getNoteAtGenericInterval(options.lowestNote, 2);
    return randomNotes(numberOfQuestions, lowestNote, options.highestNote, ['bb']);
  }

  enharmonic_equivalence(numberOfQuestions) {
    const questions = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'A', 'Bb', 'B'];
    return this.rejectDuplicates(questions, numberOfQuestions, {});
  }

  bass_lines(numberOfQuestions, options) {
    let lowestNote = 'G2';
    let highestNote = 'D3';
    let numQ = Math.ceil(numberOfQuestions * (3 / 5));
    const notes1 = randomWhiteNotes(numQ, lowestNote, highestNote, ['G', 'B', 'D'], options);
    lowestNote = 'F3';
    highestNote = 'A3';
    numQ = Math.ceil(numberOfQuestions * (2 / 5));
    const notes2 = randomWhiteNotes(numQ, lowestNote, highestNote, ['F', 'A'], options);

    return this.rejectDuplicates(notes1.concat(notes2), numberOfQuestions);
  }

  bass_spaces(numberOfQuestions, options) {
    const lowestNote = 'A2';
    const highestNote = 'G3';
    return randomWhiteNotes(
      numberOfQuestions,
      lowestNote,
      highestNote,
      ['A', 'C', 'E', 'G'],
      options
    );
  }

  bass_high_legers(numberOfQuestions, options) {
    const lowestNote = 'B3';
    const highestNote = 'G4';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  bass_low_legers(numberOfQuestions, options) {
    const lowestNote = 'C2';
    const highestNote = 'G2';
    return randomWhiteNotes(numberOfQuestions, lowestNote, highestNote, undefined, options);
  }

  /**
   * @typedef {object} IntervalGenerationOptions
   * @param {Note | string} lowestNote
   * @param {Note | string} highestNote
   * @param {string[]} acceptableFirstNoteAccidentals
   * @param {string[]} acceptableSecondNoteAccidentals
   * @param {boolean} mustFitSingleClef
   * @param {string[]} [acceptableClefs]
   * @param {("notes" | "interval" | "generic" | "ignoreAccidentals" | "enharmonic")} comparisonType
   */

  /**
   * Generate interval questions
   *
   * @param {number} numberOfQuestions How many questions to generate
   * @param {Partial<IntervalGenerationOptions>} options Options for restricting intervals
   * @param {Interval | string} interval string or Interval class
   * @param {(1 | -1)} [direction] - If not provided, both directions included!
   * @returns {IntervalQuestion[]}
   */
  interval_helper(numberOfQuestions, options, interval, direction) {
    interval = new Interval(interval);
    // If direction is not provided, we get it in both directions:
    const intervals = direction
      ? [interval.setDirection(direction)]
      : [interval.clone().setDirection(1), interval.clone().setDirection(-1)];

    return randomIntervalQuestions(numberOfQuestions, intervals, options);

    // this.log('interval_helper', numberOfQuestions, options, interval, direction);

    // const startingPool = (numberOfQuestions || 1) * 2;

    // let intervalQuestions = [];
    // const lowestNote = new Note(options.lowestNote || 'E2');
    // const highestNote = new Note(options.highestNote || 'C6');
    // const intervalDown = new Interval(interval).setDirection(-1);
    // const intervalUp = new Interval(interval).setDirection(1);

    // // Generate half of the questions descending:
    // const paddedLowestNote = lowestNote.getNoteAt(intervalUp);
    // const paddedHighestNote = highestNote.getNoteAt(intervalDown);

    // // eslint-disable-next-line no-shadow
    // const generateIntervals = (lowestNote, highestNote, numberOfQuestions, interval) => {
    //   const notes = randomNotes(
    //     ~~(startingPool * 1.5),
    //     lowestNote,
    //     highestNote,
    //     ['#', 'b', '', ''],
    //     options
    //   );
    //   return notes
    //     .map((note) => {
    //       // Check and be sure that interval is possible first:
    //       if (!note.getNoteAt(interval)) {
    //         console.log(`rejected ${note} because note @ ${interval} is impossible`);
    //         return false;
    //       }

    //       // If options.mustFitSingleClef, elminate any that don't fit
    //       // on either treble or bass::
    //       if (options.mustFitSingleClef) {
    //         console.log(`rejected ${note} because note @ ${interval} won't fit within one clef.`);
    //         const nh = new NoteHolder([note, note.getNoteAt(interval)]);
    //         if (nh.prefersClef().length === 0) return false;
    //       }

    //       return new IntervalQuestion(interval, note, { comparisonType: 'notes' });
    //     })
    //     .filter((q) => q);
    // };

    // // Ascending intervals:
    // if (direction !== -1) {
    //   intervalQuestions = intervalQuestions.concat(
    //     generateIntervals(lowestNote, paddedHighestNote, startingPool, intervalUp)
    //   );
    // }
    // if (direction !== 1) {
    //   intervalQuestions = intervalQuestions.concat(
    //     generateIntervals(paddedLowestNote, highestNote, startingPool, intervalDown)
    //   );
    // }

    // intervalQuestions = this.rejectDuplicates(intervalQuestions, numberOfQuestions);

    // this.logArray(intervalQuestions);

    // return intervalQuestions;
  }

  half_steps_piano(numberOfQuestions, options) {
    return this.interval_helper(numberOfQuestions, options, 'm2');
  }

  whole_steps_piano(numberOfQuestions, options) {
    return this.interval_helper(numberOfQuestions, options, 'M2');
  }

  ws(numberOfQuestions, options) {
    return this.interval_helper(numberOfQuestions, options, 'M2');
  }

  chs(numberOfQuestions, options) {
    return this.interval_helper(numberOfQuestions, options, 'A1');
  }

  dhs(numberOfQuestions, options) {
    return this.interval_helper(numberOfQuestions, options, 'm2');
  }

  compound_intervals(numberOfQuestions, options) {
    const ascendingMajor = this.ascending_major_perfect(numberOfQuestions, options);
    const ascendingOthers = this.ascending_others(numberOfQuestions, options);
    const compoundIntervals = this.compoundIntervals(numberOfQuestions, options);
    const questions = [].concat(ascendingMajor, ascendingOthers, compoundIntervals);

    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  simple_and_compound_with_generics(numberOfQuestions) {
    const intervals = [
      'M2',
      'M3',
      'P4',
      'P5',
      'M6',
      'M7',
      'P8',
      'M9',
      'M10',
      'P11',
      'P12',
      'M13',
      'M14',
      'P15',
    ];

    return this.rejectDuplicates(intervals, numberOfQuestions).map((intervalString) => ({
      interval: new Interval(intervalString),
    }));
  }

  compoundIntervals(numberOfQuestions, options) {
    const intervals = [
      'm9',
      'M9',
      'm10',
      'M10',
      'P11',
      'A11',
      'd12',
      'P12',
      'm13',
      'M13',
      'd14',
      'm14',
      'M14',
      'P15',
    ];
    let questions = [];

    intervals.forEach((interval) => {
      questions = questions.concat(this.interval_helper(numberOfQuestions, options, interval, 1));
    });

    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  generic_intervals(numberOfQuestions, options) {
    const intervals = ['P1', 'M2', 'M3', 'P4', 'P5', 'M6', 'M7', 'P8'];
    let questions = [];
    const questionsPerInterval = Math.ceil(numberOfQuestions / intervals.length);

    options = options || {};
    options.comparisonType = 'generic';

    intervals.forEach((interval) => {
      const intervalQuestions = this.interval_helper(questionsPerInterval, options, interval);
      questions = questions.concat(intervalQuestions);
    });

    return questions;
  }

  interval_groups(numberOfQuestions) {
    const intervals = ['P1', 'M2', 'M3', 'P4', 'P5', 'M6', 'M7', 'P8'];
    return this.rejectDuplicates(intervals, numberOfQuestions);
  }

  /*
  ascending_major_perfect : 51,
  ascending_others : 52,
  descending_minor_perfect : 53,
  descending_others : 54,
  */
  // GCR: TEST THIS!!!
  ascending_major_perfect(numberOfQuestions, options) {
    const intervals = ['M2', 'M3', 'P4', 'P5', 'M6', 'M7', 'P8'];
    let questions = [];

    intervals.forEach((interval) => {
      questions = questions.concat(this.interval_helper(numberOfQuestions, options, interval, 1));
    });

    return questions;
  }

  ascending_others(numberOfQuestions, options) {
    const intervals = ['m2', 'm3', 'm6', 'm7', 'A4', 'd5'];
    let questions = [];

    intervals.forEach((interval) => {
      const intervalQuestions = this.interval_helper(numberOfQuestions, options, interval, 1);
      questions = questions.concat(intervalQuestions);
    });

    return questions;
  }

  descending_minor_perfect(numberOfQuestions, options) {
    const intervals = ['-m2', '-m3', '-m6', '-m7'];
    let questions = [];

    intervals.forEach((interval) => {
      questions = questions.concat(this.interval_helper(numberOfQuestions, options, interval, -1));
    });

    return questions;
  }

  descending_others(numberOfQuestions, options) {
    const intervals = ['-M2', '-M3', '-P4', '-P5', '-M6', '-M7', '-P8', '-A4', '-d5'];
    let questions = [];

    intervals.forEach((interval) => {
      questions = questions.concat(this.interval_helper(numberOfQuestions, options, interval, -1));
    });

    return questions;
  }

  interval_inversion(numberOfQuestions) {
    return this.rejectDuplicates(QuestionGenerator.allIntervalsArray, numberOfQuestions).map(
      (q) => ({
        interval: new Interval(q),
      })
    );
  }

  major_scales_staff(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'Major',
      piano: false,
    });
  }

  natural_minor(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'naturalMinor',
      piano: false,
    });
  }

  harmonic_minor(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'harmonicMinor',
      piano: false,
    });
  }

  melodic_minor(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'melodicMinorAscending',
      piano: false,
    });
  }

  major_scales_piano(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'Major',
      piano: true,
    });
  }

  natural_minor_piano(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'naturalMinor',
      piano: true,
    });
  }

  harmonic_minor_piano(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'harmonicMinor',
      piano: true,
    });
  }

  melodic_minor_piano(numberOfQuestions) {
    return this.scale_helper(numberOfQuestions, {
      mode: 'melodicMinorAscending',
      piano: true,
    });
  }

  // #### Key Signatures:
  sharp_majors(numberOfQuestions) {
    const acceptableKeys = sharpMajors;
    const questions = [];

    for (let i = 0; i < acceptableKeys.length; i++) {
      questions.push(`${acceptableKeys[i]}/M`);
    }
    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  flat_majors(numberOfQuestions) {
    const acceptableKeys = flatMajors;
    const questions = [];

    for (let i = 0; i < acceptableKeys.length; i++) {
      questions.push(`${acceptableKeys[i]}/M`);
    }
    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  sharp_minors(numberOfQuestions) {
    const acceptableKeys = sharpMinors;
    const questions = [];

    for (let i = 0; i < acceptableKeys.length; i++) {
      questions.push(`${acceptableKeys[i]}/m`);
    }
    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  flat_minors(numberOfQuestions) {
    const acceptableKeys = flatMinors;
    const questions = [];

    for (let i = 0; i < acceptableKeys.length; i++) {
      questions.push(`${acceptableKeys[i]}/m`);
    }
    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  relative_keys(numberOfQuestions) {
    const flats = flatMajors.map((key, i) => {
      if (Math.random() > 0.5) {
        return `${flatMinors[i]}/minor/relative`;
      }
      return `${flatMajors[i]}/Major/relative`;
    });
    const sharps = sharpMajors.map((key, i) => {
      if (Math.random() > 0.5) {
        return `${sharpMinors[i]}/minor/relative`;
      }
      return `${sharpMajors[i]}/Major/relative`;
    });
    return this.rejectDuplicates(flats.concat(sharps), numberOfQuestions);
  }

  parallel_keys(numberOfQuestions) {
    const keys = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#', 'F', 'Bb', 'Eb', 'Ab'];
    const questions = keys.map((key) => {
      const quality = Math.random() < 0.5 ? 'Major' : 'minor';
      return `${key}/${quality}/parallel`;
    });

    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  imaginary_keys(numberOfQuestions) {
    const keys = [
      'G#/M',
      'D#/M',
      'A#/M',
      'E#/M',
      'B#/M',
      'Fx/M', // 'Cx/m',
      'Fb/M',
      'Bbb/M', // 'Ebb/M', 'Abb/M', 'Dbb/M', 'Gbb/M', 'Cbb/M',
      'E#/m',
      'B#/m',
      'Fx/m', // 'Cx/m', 'Gx/m', 'Dx/m', 'Ax/m',
      'Db/m',
      'Gb/m',
      'Cb/m',
      'Fb/m',
      'Bbb/m', // 'Ebb/m', 'Abb/m',
    ];

    return this.rejectDuplicates(keys, numberOfQuestions);
  }

  getRelativeOrParallelKeysQuestions(numberOfQuestions) {
    const flatMajorQuestions = this.flat_majors(numberOfQuestions);
    const flatMinorQuestions = this.flat_minors(numberOfQuestions);
    const sharpMajorQuestions = this.sharp_majors(numberOfQuestions);
    const sharpMinorQuestions = this.sharp_minors(numberOfQuestions);
    let questions = [];

    questions = questions.concat(
      flatMajorQuestions,
      flatMinorQuestions,
      sharpMajorQuestions,
      sharpMinorQuestions
    );

    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  // ###Triads, seventh chords, roman numeral
  triad_inversion(numberOfQuestions) {
    const questions = [];
    let question;
    const rootNotes = randomWhiteNotes(numberOfQuestions, 'A3', 'E5', undefined);
    const qualities = ['M', 'm', '+', 'o'];
    const inversions = ['1', '2'];

    for (let i = 0; i < numberOfQuestions; i++) {
      const note = rootNotes[this.getRandom(rootNotes.length)];
      const rootNote = note.letter + note.accidental + note.octave;
      const quality = qualities[this.getRandom(qualities.length)];
      const inversion = inversions[this.getRandom(inversions.length)];

      question = `${rootNote}/${quality}/${inversion}`;
      questions.push(question);
    }

    return questions;
  }

  seventh_chord_inversions(numberOfQuestions) {
    const questions = [];
    let question;
    const rootNotes = randomWhiteNotes(numberOfQuestions, 'A3', 'E5', undefined);
    const qualities = ['M7', 'm7', '+7', 'o7', 'o/7'];
    const inversions = ['1', '2', '3'];

    for (let i = 0; i < numberOfQuestions; i++) {
      const note = rootNotes[this.getRandom(rootNotes.length)];
      const rootNote = note.letter + note.accidental + note.octave;
      const quality = qualities[this.getRandom(qualities.length)];
      const inversion = inversions[this.getRandom(inversions.length)];

      question = `${rootNote}/${quality}/${inversion}`;
      questions.push(question);
    }

    return questions;
  }

  getChords(skillNames, numberOfQuestions, et = false) {
    // Verify & format skillNames to array:
    if (!skillNames) throw new Error('Must provide skill names!');
    if (!utils.isArray(skillNames)) skillNames = [skillNames];
    skillNames = skillNames.map(Skills.makeString);

    const skillsToQuality = !et ? QuestionGenerator._chordSkills : QuestionGenerator._etChordSkills;
    const qualities = skillNames
      .map((skill) => skillsToQuality[skill])
      .filter((quality) => quality);
    if (!qualities.length)
      throw new Error('At least one valid skill must be provided to getChords.');
    const questionsPerQuality = Math.ceil(numberOfQuestions / qualities.length);
    let questions = [];
    questions = qualities.map((quality) => {
      const chordsByQuality = Chord.getAllChordsByQuality();
      const matchedChords = utils.shuffle(chordsByQuality[quality]);
      return utils.firstNUnique(matchedChords, questionsPerQuality);
    });

    questions = utils.flatten(questions);

    const invertQuestions = skillNames.find(
      (skill) => skill.search('inversion') !== -1 || skill.search('figure') !== -1
    );

    if (invertQuestions) questions = this.invertQuestions(questions);
    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  getChordSkills(chord) {
    chord = chord instanceof Chord ? chord : new Chord(chord);
    const skills = [];
    const chordSkills = QuestionGenerator._chordSkills;
    Object.keys(chordSkills).forEach((skill) => {
      const skillQuality = Chord.normalizeQuality(chordSkills[skill]).quality;

      if (skillQuality === chord.quality) {
        skills.push(skill);
      }
    });

    return skills;
  }

  getChordInversionSkills(chord, figures) {
    const skills = [];
    if (chord.inversion) {
      skills.push(chord.isSeventh ? 'seventh_chord_inversion' : 'triad_inversion');
      if (figures) {
        skills.push(chord.isSeventh ? 'seventh_chord_figured_bass' : 'triad_figured_bass');
      }
    }
    return skills;
  }

  invertQuestions(questions) {
    const initialInversion = ~~(Math.random() * 4);
    return questions.map((chordName, index) => {
      const isSeventh = chordName.search('7') !== -1;
      const inversion = (initialInversion + index) % (isSeventh ? 4 : 3);
      let figures = Chord.inversionToFigures(inversion, isSeventh);
      // Diminished triads only happen in first inversion... but
      // we'll allow a few randoms to be not too obvious about it:
      if (!isSeventh && chordName.search('dim') !== -1) {
        figures = Math.random() < 0.55 - 0.55 * 0.33 ? '6' : figures;
      }

      // if 7th chord, be sure we get rid of first 7:
      chordName = chordName.replace('7', '');

      if (figures) chordName += ` ${figures}`;
      return chordName;
    });
  }

  getRomanQuestions(skillName, numberOfQuestions) {
    let questions = [];

    switch (skillName) {
      case 'roman_qualities_in_major':
      case 'roman_seventh_chords_in_major':
        questions = this.generateRomanQuestions('M', numberOfQuestions, true);
        break;
      case 'roman_id_in_major':
        questions = this.generateRomanQuestions('M', numberOfQuestions, false);
        break;
      case 'roman_qualities_in_minor':
      case 'roman_seventh_chords_in_minor':
        questions = this.generateRomanQuestions('m', numberOfQuestions, true);
        break;
      case 'roman_id_in_minor':
        questions = this.generateRomanQuestions('m', numberOfQuestions, false);
        break;
      default:
        return [];
    }

    return questions;
  }

  generateChordQuestions(quality, inversion, numberOfQuestions) {
    const questions = [];
    let question;
    const rootNotes = randomWhiteNotes(numberOfQuestions, 'A3', 'E5', undefined);

    for (let i = 0; i < numberOfQuestions; i++) {
      const note = rootNotes[this.getRandom(rootNotes.length)];
      const rootNote = note.letter + note.accidental + note.octave;

      question = `${rootNote}/${quality}/${inversion}`;
      questions.push(question);
    }

    return utils.shuffle(questions);
  }

  /**
   * get roman_numerals questions from skills
   *
   * @param {number} numberOfQuestions
   * @param {(number|string)[]} skills
   * @param {object} options
   * @param {boolean} options.includeSpacingSkills = false, include spacing skills
   * @param {boolean} options.noRaiseTheLeadingTone = false, but make false for ID.
   * @returns {{ rn: RomanNumeral, skills: string[]}[]}
   */
  roman_numerals(numberOfQuestions, skills, options = {}) {
    skills = skills.map(Skills.makeString).filter((s) => s);
    const triads = ['triad_rn_major', 'triad_rn_minor'].find((skill) => skills.includes(skill));
    const sevenths = ['seventh_rn_major', 'seventh_rn_minor'].find((skill) =>
      skills.includes(skill)
    );
    const majors = skills.find((skill) => skill.includes('major'));
    const minors = skills.find((skill) => skill.includes('minor'));
    const open = skills.find((skill) => skill.includes('open'));
    const close = skills.find((skill) => skill.includes('close'));
    const spacingSkills = [open, close].filter((n) => n);

    const keyQuality = majors && minors ? 'both' : minors ? 'minor' : 'Major';
    const inversions = skills.find((skill) => skill.includes('inversion'));
    const chordTypes = triads && sevenths ? 'both' : sevenths ? 'sevenths' : 'triads';

    this.log(
      `triads: ${triads}`,
      `sevenths: ${sevenths}`,
      `majors: ${majors}`,
      `minors: ${minors}`,
      `open: ${open}`,
      `close: ${close}`,
      `spacingSkills: ${spacingSkills}`,
      `inversions: ${inversions}`
    );

    return this.generateRomanQuestions(numberOfQuestions, {
      keyQuality,
      inversions,
      chordTypes,
    }).map((q) => {
      if (options.includeSpacingSkills && spacingSkills.length) {
        const spacing = spacingSkills[~~(Math.random() * spacingSkills.length)];
        q.skills.push(spacing);
        q.spacing = spacing.includes('open') ? 'open' : 'close';
      }
      if (!options.noRaiseTheLeadingTone) {
        q.skills = q.skills.filter((skill) => skill !== 'raise_the_leading_tone');
      }
      return q;
    });
  }

  /**
   * get roman numeral questions in the format "key:roman"
   *
   * @param {integer} numberOfQuestions (>1)
   * @param {object} options chord settings
   * @param {string} options.keyQuality 'Major', 'minor', 'both'; default 'both'
   * @param {boolean} options.inversions default true
   * @param {string} options.chordTypes 'triads', 'sevenths', 'both'; default 'both'
   * @returns {{rn: RomanNumeral, skills: string[]}[]}
   */
  generateRomanQuestions(numberOfQuestions, options = {}) {
    return generateRomanQuestions(numberOfQuestions, options);
  }

  /**
   * return skills for a RN question
   *
   * @param options
   * @param {string} options.sd
   * @param {number} options.inversion
   * @param {boolean} options.isSeventh
   * @param {boolean} options.keyIsMajor
   * @param {boolean} [options.noRaiseTheLeadingTone]
   * @returns {string[]}
   */
  getRNSkills(options) {
    return getRNSkills(options);
  }

  getRandom(max) {
    return Math.floor(max * Math.random());
  }

  // ### Rhythm Functions:
  basic_rhythm_math(numberOfQuestions) {
    // Populate all questions:
    let questions = this.rhythm_math_helper(true, false);

    while (questions.length < numberOfQuestions) {
      questions = questions.concat(this.rhythm_math_helper(true, false));
    }

    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  dotted_rhythm_math(numberOfQuestions) {
    // Populate all questions:
    let questions = this.rhythm_math_helper(false, true);

    while (questions.length < numberOfQuestions) {
      questions = questions.concat(this.rhythm_math_helper(false, true));
    }

    return this.rejectDuplicates(questions, numberOfQuestions);
  }

  rhythm_math_helper(simples, dotteds) {
    let r;
    const qlist = [];
    const simpleStrings = ['w', 'h', 'q', '8', '16'];
    const dottedStrings = ['w.', 'h.', 'q.', '8.', '16.'];
    const simple = simpleStrings.map((s) => new Rhythm(s));
    const dotted = dottedStrings.map((s) => new Rhythm(s));

    for (let i = 0; i < simple.length; i++) {
      for (let j = i; j < simple.length; j++) {
        if (
          simples &&
          i !== j &&
          simple[i].getFraction().divide(simple[j].getFraction()).simplify().denominator === 1
        ) {
          r = Math.random() > 0.6 ? 'r' : '';
          const qString = `${simple[j] + r}/${simple[i]}${r}`;
          qlist.push(qString);
        }
        // if dotted:
        // check dotted / simple
        if (
          dotteds &&
          dotted[i].getFraction().divide(simple[j].getFraction()).simplify().denominator === 1
        ) {
          r = Math.random() > 0.6 ? 'r' : '';
          qlist.push(`${simple[j] + r}/${dotted[i]}${r}`);
        }
        // check dotted/ dotted
        if (
          dotteds &&
          i !== j &&
          dotted[i].getFraction().divide(dotted[j].getFraction()).simplify().denominator === 1
        ) {
          r = Math.random() > 0.6 ? 'r' : '';
          qlist.push(`${dotted[j] + r}/${dotted[i]}${r}`);
        }
      }
    }
    return qlist;
  }

  // ### Ear Training
  scaleDegreeHelper(numberOfQuestions, scale_degree_list) {
    return this.rejectDuplicates(scale_degree_list, numberOfQuestions);
  }

  // ### Helper functions:

  scale_helper(numberOfQuestions, options) {
    // Options:
    // - mode
    // - piano (no enharmonics for piano scales)
    // - direction: 1 (all ascending), -1 (all descending) otherwise random.

    // if options.piano limit enharmonics:
    let maxSharps = 7;
    let maxFlats = 7;
    let sharpKeyNames = [];
    let flatKeyNames = [];
    if (options.piano) {
      const enharmonicSharpsVsFlats = Math.floor(Math.random() * 4);
      maxSharps = 4 + enharmonicSharpsVsFlats;
      maxFlats = 7 - enharmonicSharpsVsFlats;
    }

    if (options.mode === 'Major') {
      sharpKeyNames = sharpMajors.slice(0, maxSharps + 1);
      // start with 1 because 0 equals no sharps/flats is shared:
      flatKeyNames = flatMajors.slice(1, maxFlats + 1);
    } else {
      sharpKeyNames = sharpMinors.slice(0, maxSharps + 1);
      // start with 1 because 0 equals no sharps/flats is shared:
      flatKeyNames = flatMinors.slice(1, maxFlats + 1);
    }

    const keyNames = sharpKeyNames.concat(flatKeyNames);
    const scales = [];
    keyNames.forEach((keyNote) => {
      let note = new Note(keyNote);
      if (note.isGreaterThan('G4')) note = note.getNoteAt('-P8');
      scales.push(new Scale(note, options.mode));
    }, this);

    return this.rejectDuplicates(scales, numberOfQuestions);

    // GCR finish me
    //  - imaginary scales? For now, no. When we get to "imaginary keys" skill, sure.
  }

  // ### Multiple Compatible Skils:

  multipleSkillsGenerate(objectsArray, numberOfQuestions, options) {
    // Debug:
    if (this.debug) {
      console.log('multipleSkillsGenerate: ');
      console.log('objectsArray: ');
      console.log(objectsArray);
      console.log(`numberOfQuestions: ${numberOfQuestions}`);
      console.log('options: ');
      console.log(options);
    }

    // Sum probabilities for each if they've been sent:
    const probabilityTotal = objectsArray.reduce((accum, el) => {
      if (!utils.isObject(el)) return 0;
      return accum + (el.probability || 0);
    }, 0);

    const questions = objectsArray.reduce((accum, el) => {
      if (!utils.isObject(el)) {
        el = { skill: el };
      }
      el.numberOfQuestions = probabilityTotal
        ? Math.ceil((numberOfQuestions * (el.probability || 1)) / probabilityTotal)
        : Math.ceil(numberOfQuestions / objectsArray.length);
      const generatorName = Skills.getSkill(el.skill).textId;
      if (!utils.isFunction(this[generatorName])) {
        throw new Error(
          `Unable to generate questions for ${el.skill}. No generator found for that skill.`
        );
      }
      let newQuestions = this[generatorName](el.numberOfQuestions * 2, el.options || options);
      newQuestions = this.rejectDuplicates(
        newQuestions,
        el.numberOfQuestions,
        el.options || options
      );
      return accum.concat(newQuestions);
    }, []);

    return this.rejectDuplicates(questions, numberOfQuestions, options);
  }

  // ### Helper Methods:

  /**
   * rejectDuplicates on an array of questions and return enough questions
   * with as little duplication as possible, and nicely spread-out repetition
   * if there is repetition.
   *
   * @param {any[]} array of questions
   * @param {number} numberOfQuestions
   * @param {object} options
   * @param {Function} options.equals comparator for uniqueness
   * @param {Function} options.adjacentEquality comparator for adjacency
   * @param {boolean} [options.ignoreOctave] if question objects have an
   *  equalsIgnoreOctave function, use that to compare.
   * @returns {any[]} an array of questions
   */
  rejectDuplicates(array, numberOfQuestions, options = {}) {
    return rejectDuplicates(array, numberOfQuestions, options);
  }

  logArray(array) {
    if (this.debug) {
      array.forEach((question, index) => {
        console.log(`${index}: ${question}`);
      }, this);
    }
  }

  randomNumerals(numberOfQuestions, keyQuality) {
    const numerals = [];

    if (keyQuality === 'M') {
      numerals.push('I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii');
    } else if (keyQuality === 'm') {
      numerals.push('i', 'ii', 'III', 'iv', 'V', 'VI', 'vii');
    }

    return numerals.slice(0, numberOfQuestions);
  }

  randomFigures(numberOfQuestions, seventh) {
    const figures = [];

    if (seventh === true) {
      figures.push('', '6', '64');
    } else {
      figures.push('7', '65', '43', '42');
    }

    return figures;
  }
}

QuestionGenerator.allIntervalsArray = [
  'm2',
  'M2',
  'A2',
  'd3',
  'm3',
  'M3',
  'P4',
  'A4',
  'd5',
  'P5',
  'A5',
  'm6',
  'M6',
  'd7',
  'm7',
  'M7',
];

QuestionGenerator._chordSkills = {
  major_triads: 'M',
  minor_triads: 'm',
  augmented_triads: 'A',
  diminished_triads: 'o',
  major_seventh_chords: 'M7',
  minor_seventh_chords: 'm7',
  dominant_seventh_chords: '7',
  diminished_seventh_chords: 'o7',
  half_diminished_seventh_chords: 'o/7',
};

QuestionGenerator._etChordSkills = {
  major_triad_et: 'M',
  minor_triad_et: 'm',
  augmented_triad_et: 'A',
  diminshed_triad_et: 'o',
  maj_7_chords_et: 'M7',
  min_7_chords_et: 'm7',
  dom_7_chords_et: '7',
  dim_7_chords_et: 'o7',
  half_dim_7_chords_et: 'o/7',
};

// ### Question Type Objects:

export default QuestionGenerator;
